import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { createBeybladeModel } from './BeybladeModel.js';
import { createArena } from './ArenaBuilder.js';
import { BeybladePhysics } from './BeybladePhysics.js';
import { EffectsManager } from './Effects.js';
import { ARENAS, PHYSICS } from './PartData.js';
import * as Audio from './Audio.js';

// ── Main battle scene ──
export class BattleScene {
  constructor() {
    this.canvas = document.getElementById('game-canvas');
    this.renderer = new THREE.WebGLRenderer({ canvas: this.canvas, antialias: true });
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.2;

    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(50, window.innerWidth / window.innerHeight, 0.1, 200);

    this.controls = null;
    this.effects = null;
    this.arenaMesh = null;
    this.bey1Mesh = null;
    this.bey2Mesh = null;
    this.bey1Physics = null;
    this.bey2Physics = null;

    this.battleActive = false;
    this.lastTime = 0;
    this.winner = null;

    this.lastCollisionTime1 = 0;
    this.lastCollisionTime2 = 0;
    this.lastWallTime1 = 0;
    this.lastWallTime2 = 0;
    this.trailTimer = 0;

    this._resize = () => this._onResize();
    window.addEventListener('resize', this._resize);
  }

  _onResize() {
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
  }

  initBattle(bey1Config, bey2Config, arenaType) {
    // Clear old scene
    this._clearScene();

    this._onResize();
    const arena = ARENAS[arenaType];

    // ── Lighting ──
    this.scene.fog = new THREE.FogExp2(arena.envColor, 0.015);
    this.scene.background = new THREE.Color(arena.envColor);

    const ambient = new THREE.AmbientLight(0x404060, 0.6);
    this.scene.add(ambient);

    const sun = new THREE.DirectionalLight(0xffffff, 1.8);
    sun.position.set(5, 15, 5);
    sun.castShadow = true;
    sun.shadow.mapSize.set(2048, 2048);
    sun.shadow.camera.near = 0.5;
    sun.shadow.camera.far = 50;
    sun.shadow.camera.left = -15;
    sun.shadow.camera.right = 15;
    sun.shadow.camera.top = 15;
    sun.shadow.camera.bottom = -15;
    this.scene.add(sun);

    // Colored accent lights
    const accent1 = new THREE.PointLight(new THREE.Color(bey1Config.color), 1, 20);
    accent1.position.set(-5, 5, 0);
    this.scene.add(accent1);

    const accent2 = new THREE.PointLight(new THREE.Color(bey2Config.color), 1, 20);
    accent2.position.set(5, 5, 0);
    this.scene.add(accent2);

    // Volcano glow
    if (arena.hasLava) {
      const lavaLight = new THREE.PointLight(0xff4400, 2, 15);
      lavaLight.position.set(0, 0.5, 0);
      this.scene.add(lavaLight);
    }

    // ── Camera ──
    this.camera.position.set(0, 12, 14);
    this.camera.lookAt(0, 0, 0);

    this.controls = new OrbitControls(this.camera, this.canvas);
    this.controls.target.set(0, 0, 0);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.05;
    this.controls.maxPolarAngle = Math.PI / 2.1;
    this.controls.minDistance = 5;
    this.controls.maxDistance = 25;
    this.controls.update();

    // ── Arena ──
    this.arenaMesh = createArena(arenaType);
    this.scene.add(this.arenaMesh);

    // ── Beyblades ──
    this.bey1Mesh = createBeybladeModel(bey1Config);
    this.bey2Mesh = createBeybladeModel(bey2Config);
    this.scene.add(this.bey1Mesh);
    this.scene.add(this.bey2Mesh);

    // Start positions
    const startDist = arena.radius * 0.5;
    this.bey1Physics = new BeybladePhysics(bey1Config, { x: -startDist, z: 0 }, arenaType);
    this.bey2Physics = new BeybladePhysics(bey2Config, { x: startDist, z: 0 }, arenaType);

    // Position meshes at start
    this.bey1Physics.applyToMesh(this.bey1Mesh);
    this.bey2Physics.applyToMesh(this.bey2Mesh);

    // ── Effects ──
    this.effects = new EffectsManager(this.scene);

    // Reset state
    this.battleActive = false;
    this.winner = null;
    this.lastTime = 0;
  }

  startCountdown(onComplete) {
    const overlay = document.getElementById('launch-overlay');
    const content = overlay.querySelector('.launch-content h2');
    const subtitle = overlay.querySelector('.launch-content p');
    overlay.classList.remove('hidden');

    let count = 3;
    content.textContent = count;
    subtitle.textContent = 'Get Ready!';
    Audio.playCountdown();

    const interval = setInterval(() => {
      count--;
      if (count > 0) {
        content.textContent = count;
        Audio.playCountdown();
      } else if (count === 0) {
        content.textContent = 'GO!';
        subtitle.textContent = 'Let It Rip!';
        Audio.playGo();
        Audio.playLaunch();
      } else {
        clearInterval(interval);
        overlay.classList.add('hidden');
        onComplete();
      }
    }, 1000);
  }

  beginBattle() {
    this.battleActive = true;
    this.lastTime = performance.now();

    // Launch beyblades toward center
    this.bey1Physics.launch(0, 0);
    this.bey2Physics.launch(0, 0);

    Audio.startSpinSound();

    // Show HUD
    document.getElementById('battle-hud').classList.remove('hidden');
    document.getElementById('battle-result').classList.add('hidden');

    this._gameLoop();
  }

  _gameLoop() {
    if (!this.battleActive) return;

    const now = performance.now();
    const dt = Math.min((now - this.lastTime) / 1000, 0.05); // cap at 50ms
    this.lastTime = now;

    // ── Physics update ──
    this.bey1Physics.update(dt, this.bey2Physics);
    this.bey2Physics.update(dt, this.bey1Physics);

    // ── Apply to meshes ──
    this.bey1Physics.applyToMesh(this.bey1Mesh);
    this.bey2Physics.applyToMesh(this.bey2Mesh);

    // ── Collision effects ──
    if (this.bey1Physics.lastCollision &&
        this.bey1Physics.lastCollision > this.lastCollisionTime1) {
      this.lastCollisionTime1 = this.bey1Physics.lastCollision;
      const midPoint = new THREE.Vector3().addVectors(
        this.bey1Physics.position, this.bey2Physics.position
      ).multiplyScalar(0.5);

      const isCrit = this.bey1Physics.lastCritical || this.bey2Physics.lastCritical;

      if (isCrit) {
        // CRITICAL HIT — big golden flash + double sparks
        this.effects.spawnCollisionSparks(midPoint, 0xffdd00, 0xff8800);
        this.effects.spawnCollisionSparks(midPoint, 0xffffff, 0xffcc00);
        this.effects.spawnBurst(midPoint);
        Audio.playCollision(1.0);
      } else {
        this.effects.spawnCollisionSparks(
          midPoint,
          new THREE.Color(this.bey1Physics.config.color).getHex(),
          new THREE.Color(this.bey2Physics.config.color).getHex()
        );
        const intensity = Math.min(1, Math.abs(
          this.bey1Physics.velocity.length() + this.bey2Physics.velocity.length()
        ) / 10);
        Audio.playCollision(intensity);
      }
    }

    // ── Wall hit effects ──
    if (this.bey1Physics.lastWallHit > this.lastWallTime1) {
      this.lastWallTime1 = this.bey1Physics.lastWallHit;
      Audio.playWallHit();
    }
    if (this.bey2Physics.lastWallHit > this.lastWallTime2) {
      this.lastWallTime2 = this.bey2Physics.lastWallHit;
      Audio.playWallHit();
    }

    // ── Trail effects ──
    this.trailTimer += dt;
    if (this.trailTimer > 0.08) {
      this.trailTimer = 0;
      if (this.bey1Physics.spinRPM > 1000) {
        this.effects.addTrailRing(
          this.bey1Physics.position,
          new THREE.Color(this.bey1Physics.config.color).getHex(),
          0.4
        );
      }
      if (this.bey2Physics.spinRPM > 1000) {
        this.effects.addTrailRing(
          this.bey2Physics.position,
          new THREE.Color(this.bey2Physics.config.color).getHex(),
          0.4
        );
      }
    }

    // ── Effects update ──
    this.effects.update(dt);

    // ── HUD update ──
    this._updateHUD();

    // ── Audio ──
    Audio.updateSpinSound(this.bey1Physics.spinRPM, this.bey2Physics.spinRPM);

    // ── Check win condition ──
    if (!this.bey1Physics.alive || !this.bey2Physics.alive) {
      this._endBattle();
      return;
    }

    // ── Camera shake on collision ──
    if (this.bey1Physics.lastCollision &&
        now - this.bey1Physics.lastCollision < 100) {
      this.camera.position.x += (Math.random() - 0.5) * 0.05;
      this.camera.position.y += (Math.random() - 0.5) * 0.03;
    }

    // ── Controls ──
    if (this.controls) this.controls.update();

    // ── Render ──
    this.renderer.render(this.scene, this.camera);
    requestAnimationFrame(() => this._gameLoop());
  }

  _updateHUD() {
    const maxRPM = PHYSICS.LAUNCH_SPIN;
    const pct1 = Math.max(0, this.bey1Physics.spinRPM / maxRPM * 100);
    const pct2 = Math.max(0, this.bey2Physics.spinRPM / maxRPM * 100);

    document.getElementById('p1-spin').style.width = pct1 + '%';
    document.getElementById('p2-spin').style.width = pct2 + '%';
    document.getElementById('p1-rpm').textContent = Math.round(this.bey1Physics.spinRPM) + ' RPM';
    document.getElementById('p2-rpm').textContent = Math.round(this.bey2Physics.spinRPM) + ' RPM';

    // Color change at low RPM
    if (pct1 < 30) document.getElementById('p1-spin').style.background = 'linear-gradient(90deg, #ff3d5a, #ff6b3d)';
    if (pct2 < 30) document.getElementById('p2-spin').style.background = 'linear-gradient(90deg, #ff3d5a, #ff6b3d)';
  }

  _endBattle() {
    this.battleActive = false;
    Audio.stopSpinSound();

    // Determine winner
    let resultText = '';
    if (this.bey1Physics.burstOut) {
      resultText = 'Player 2 Wins!\nBurst Finish!';
      this.effects.spawnBurst(this.bey1Physics.position);
      Audio.playBurst();
    } else if (this.bey2Physics.burstOut) {
      resultText = 'Player 1 Wins!\nBurst Finish!';
      this.effects.spawnBurst(this.bey2Physics.position);
      Audio.playBurst();
    } else if (this.bey1Physics.knockedOut) {
      resultText = 'Player 2 Wins!\nRing Out!';
    } else if (this.bey2Physics.knockedOut) {
      resultText = 'Player 1 Wins!\nRing Out!';
    } else if (!this.bey1Physics.alive && !this.bey2Physics.alive) {
      resultText = 'Draw!\nBoth Stopped!';
    } else if (!this.bey1Physics.alive) {
      resultText = 'Player 2 Wins!\nSpin Finish!';
    } else if (!this.bey2Physics.alive) {
      resultText = 'Player 1 Wins!\nSpin Finish!';
    }

    document.getElementById('result-text').textContent = resultText;
    document.getElementById('battle-result').classList.remove('hidden');

    // Keep rendering for visual
    this._postBattleRender();
  }

  _postBattleRender() {
    if (this.battleActive) return;
    this.effects.update(0.016);
    if (this.controls) this.controls.update();
    this.renderer.render(this.scene, this.camera);
    requestAnimationFrame(() => this._postBattleRender());
  }

  _clearScene() {
    if (this.effects) this.effects.dispose();
    if (this.controls) this.controls.dispose();

    // Remove everything from scene
    while (this.scene.children.length > 0) {
      const child = this.scene.children[0];
      this.scene.remove(child);
      if (child.geometry) child.geometry.dispose();
      if (child.material) {
        if (Array.isArray(child.material)) {
          child.material.forEach(m => m.dispose());
        } else {
          child.material.dispose();
        }
      }
    }
  }

  dispose() {
    this._clearScene();
    window.removeEventListener('resize', this._resize);
  }
}
